import type { ReactElement, ReactNode } from 'react';
import { byRegion, RegionVariant } from 'app/shared-components/region';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';

interface FaqItem {
  question: string;
  answer: ReactNode;
}

// The UK writes en-GB, Canada en-CA.
const ORGANISATION = byRegion({ uk: 'organisation', canada: 'organization' });

// Canada has no bespoke package to sell yet, so this answer is UK-only.
const BESPOKE_PACKAGE_ITEM: FaqItem = {
  question: 'My website needs are more specialised. Do you have other packages?',
  answer: (
    <p>
      Yes. Alongside our standard Healthera Plus subscription, we offer a Bespoke website package
      tailored to different needs.{' '}
      <a
        href="https://healthera.co.uk/pharmacies/bespoke-website/"
        target="_blank"
        rel="noopener noreferrer"
        className="vtwo:underline"
      >
        See some examples here
      </a>
      . Contact{' '}
      <a href="mailto:sales@healthera.co.uk" className="vtwo:underline">
        sales@healthera.co.uk
      </a>{' '}
      for more details.
    </p>
  ),
};

const FAQ_ITEMS: FaqItem[] = [
  {
    question: 'How does the subscription work?',
    answer: (
      <>
        <p>
          By purchasing an upgrade, the subscription (1-year contract, paid either monthly or
          annually) replaces your existing subscription - which means you'll only pay the
          difference.
        </p>
        <p>
          This covers everything you have now (Healthera Connect, patient app) AND includes
          everything you need for your website - hosting, security, updates, and ongoing support, so
          there's nothing extra to buy or maintain. You can upgrade at any time, and annual plans
          come with a discount over paying monthly. Price is quoted per pharmacy branch.
        </p>
      </>
    ),
  },
  {
    question: 'How long does it take to launch a website?',
    answer:
      "Most pharmacies are live within 3 to 5 working days. Once you've chosen your design and uploaded your details — such as your logo and services — you're nearly there. If you'd like to build it yourself, you can have a site ready to publish the same day. If you need a hand, our team will guide you through every step.",
  },
  ...byRegion({ uk: [BESPOKE_PACKAGE_ITEM], canada: [] }),
  {
    question: `Do I get one website for my ${ORGANISATION}, or a separate site for each branch?`,
    answer: `By default, you get a single website that represents your entire ${ORGANISATION}. If you need a separate site for individual branches, please get in contact and we can help.`,
  },
  {
    question: 'I have an existing domain - how do I move the new website onto my existing domain?',
    answer: (
      // Canada is served by account managers rather than the UK support desk.
      <RegionVariant
        uk={
          <p>
            We need access to your domain to connect it with your new website. Once you're happy
            with your website, let us know by calling <a href="tel:02046342296">020 4634 2296</a>,
            emailing <a href="mailto:support@healthera.co.uk">support@healthera.co.uk</a>, or
            contacting your account manager.
          </p>
        }
        canada={
          <p>
            We need access to your domain to connect it with your new website. Once you're happy
            with your website, let us know by contacting your account manager.
          </p>
        }
      />
    ),
  },
];

export function FaqAccordion(): ReactElement {
  return (
    <Accordion type="single" collapsible className="vtwo:w-full">
      {FAQ_ITEMS.map((item) => (
        <AccordionItem key={item.question} value={item.question}>
          <AccordionTrigger className="vtwo:text-base vtwo:py-6">{item.question}</AccordionTrigger>
          <AccordionContent className="vtwo:text-gray-700 vtwo:whitespace-pre-line vtwo:flex vtwo:gap-4 vtwo:flex-col">
            {item.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
